import cx from "classnames";
import { Trans } from "react-i18next";

import { useTranslation } from "@/core/i18n";
import Beautifi from "atoms/icon/icons/beautifi.svg";
import { formatCurrency } from "toolbox/Money";
import { getBeautifiRateQuote } from "utils/beautifiPromo";

interface BeautifiRateLineProps {
  priceInCents: number;
  className?: string;
}

/**
 * Beautifi's promo line. Renders nothing outside the transaction window so no call site can
 * advertise financing checkout would refuse. Platform-level gating (Beautifi on, Affirm off) stays
 * at the call site. Visually mirrors the Affirm PaymentMethodMessagingElement it stands in for:
 * centered, boxed, primary text.
 */
export const BeautifiRateLine = ({ priceInCents, className }: BeautifiRateLineProps) => {
  const { t } = useTranslation();
  const quote = getBeautifiRateQuote(priceInCents);

  if (quote === null) {
    return null;
  }

  const beautifiMark = (
    <Beautifi
      role="img"
      aria-label={t("common.beautifiLogoLabel")}
      className="inline-block h-4 align-middle"
    />
  );

  return (
    <div
      data-testid="beautifi-rate-line"
      className={cx(
        "mx-auto w-fit rounded-xl bg-background-two px-3 py-1 text-center text-body2 text-primary",
        className,
      )}
    >
      <Trans
        components={{ beautifi: beautifiMark }}
        i18nKey="beautifiRateLine.paymentsAsLowAs"
        values={{ amount: formatCurrency(quote.monthlyPaymentInCents, 2) }}
      />
    </div>
  );
};
